// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Modifier,
  RichUtils,
  EditorState,
  convertToRaw,
  getDefaultKeyBinding,
  CompositeDecorator,
} from "draft-js";
import Editor from "draft-js-plugins-editor";
import createToolbarPlugin from "draft-js-static-toolbar-plugin";
import { isEqual, throttle, debounce } from "lodash";
import { Toastr } from "../../revvComponents";

// Components Imports
import customBlockRenderer from "./BlockStyling";
import HTMLtoDraft from "../../utils/HTMLtoDraft/HTMLtoDraft";
import getEditorHTML from "./getEditorHtml";
import getCompositeDecorator from "./decorator";
import CustomRevvToolbar from "../CustomRevvToolbar";
import EditTableApiHeaderPopup from "../DocumentItems/EditTableApiHeaderPopup";

// Actions Imports
import {
  updateComponentMeta,
  updateComponentEditorState,
  updateDocumentComponentAction,
  autoUpdateTitle,
  updateFormRowDataField,
  updateDynamicFieldsInDoc,
  updateDynamicFieldsInMasterDoc,
  updateEditorStateInSectionForClauseOptions,
  executeCommand,
  saveTempPastedImage,
} from "../../actions/DocumentActions/index";
import {
  updateTableAPICellData,
  updateTableAPIColData,
} from "../../actions/TableFieldActions/index";
import { deleteNoteByNoteId } from "../../actions/NotesActions/notesActions";
import EditorActions from "../../actions/EditorActions";

// Internal Imports
import { DOCUMENTS } from "../../constants";
import "draft-js-static-toolbar-plugin/lib/plugin.css";
import {
  checkSelectionHasEntity,
  previousCharEntity,
  removeEntityAndReplaceText,
  getSelectedContentState,
  updateContentStateForMultipleStyles,
  checkIfAnchorOrFocusPosHasEntity,
  toggleBold,
  clearEntityRanges,
} from "../../utils/draftUtils";
import addLinkPlugin from "../RevvToolBar/plugins/addLinkPlugin";
import { sanitizeHTML, emptyImage } from "../../utils/documentUtils";

import {
  shouldSaveStateImmediately,
  applyStyleInEditor,
  sortContentState,
} from "../../utils/editorUtils";
import { checkColVals } from "../../utils/tableUtils";
import splitBlock from "./helpers/splitBlock";
import { DOCUMENT_COMPONENT_TYPES } from "../../constants/document";
import { getContentStateFromRawSectionData } from "../../utils/components";
import { GlobalUtils } from "../../utils";

// Destructuring
const { DOCUMENT_MODE } = DOCUMENTS;
const {
  nukeActiveEditor,
  updateActiveEditorState,
  initActiveEditor,
  forceUpdateLocalEditorState,
} = EditorActions;
const {
  FONT_SIZE_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  FONT_LINE_HEIGHT_OPTIONS,
} = DOCUMENTS;
const staticToolbarPlugin = createToolbarPlugin();
const plugins = [staticToolbarPlugin, addLinkPlugin];
const { readDataFromLocalStorage } = GlobalUtils;

const customKeyBinding = event => {
  const keyBinding = getDefaultKeyBinding(event);
  if (event.shiftKey && event.key === "Enter") {
    // cmd+enter key command requirement for ordered/unordered list in editor
    return "handleNextLine";
  }
  if (keyBinding === "undo") {
    return "handleUndo";
  }
  if (keyBinding === "redo") {
    return "handleRedo";
  }
  return keyBinding;
};

const FONT_STYLE_OPTIONS = {
  ...FONT_SIZE_OPTIONS,
  ...FONT_FAMILY_OPTIONS,
  ...FONT_WEIGHT_OPTIONS,
  ...FONT_LINE_HEIGHT_OPTIONS,
};

const CUSTOM_STYLE_PREFIX = "FONT";
const CUSTOM_STYLE_FONT_SIZE_PREFIX = "FONTSIZE";
const CUSTOM_STYLE_FONT_COLOR_PREFIX = "FONTCOLOR";
const CUSTOM_STYLE_FONT_FAMILY_PREFIX = "FONTFAMILY";
const CUSTOM_STYLE_FONT_WEIGHT_PREFIX = "FONTWEIGHT";
const CUSTOM_STYLE_TEXT_HIGHLIGHT_PREFIX = "FONTTEXTHIGHLIGHT";

function customStyleFn(style) {
  // Getting the all styles in one single string
  const styleNames = Array.isArray(style.toJS())
    ? style.toJS().toString()
    : style.toJS();

  // Method for getting the custom style
  const applyFontStyle = styleType => {
    const styleObj = {};
    if (
      typeof styleType === "string" &&
      styleType.startsWith(CUSTOM_STYLE_PREFIX)
    ) {
      if (styleType.startsWith(CUSTOM_STYLE_FONT_SIZE_PREFIX))
        styleObj.fontSize = FONT_SIZE_OPTIONS[styleType].fontSize;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_COLOR_PREFIX))
        styleObj.color = `#${styleType.split("_")[1]}`;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_FAMILY_PREFIX))
        styleObj.fontFamily = FONT_FAMILY_OPTIONS[styleType].fontFamily;
      else if (styleType.startsWith(CUSTOM_STYLE_FONT_WEIGHT_PREFIX))
        styleObj.fontWeight = FONT_WEIGHT_OPTIONS[styleType].fontWeight;
      else if (styleType.startsWith(CUSTOM_STYLE_TEXT_HIGHLIGHT_PREFIX)) {
        if (styleType.split("_")[1] === "transparent") {
          // eslint-disable-next-line prefer-destructuring
          styleObj.backgroundColor = styleType.split("_")[1];
        } else styleObj.backgroundColor = `#${styleType.split("_")[1]}`;
      }
    }
    return styleObj;
  };

  // Check whether there are custom styles present or not
  let isCustomStylePresent = false;
  const splitedStyles = styleNames ? styleNames.split(",") : [];
  if (splitedStyles && splitedStyles.length > 0) {
    const findCusStyle = splitedStyles.find(stStyle =>
      stStyle.startsWith(CUSTOM_STYLE_PREFIX),
    );
    if (findCusStyle) {
      isCustomStylePresent = true;
    }
  }

  // If custom styles are present then, get the all font styles
  let fontStyle = {};
  if (isCustomStylePresent) {
    const stylesList = styleNames.split(",");
    fontStyle = stylesList.reduce((styles, styleName) => {
      if (typeof styleName === "string") {
        styles = {
          ...styles,
          ...applyFontStyle(styleName),
        };
      } else if (Array.isArray(styleNames)) {
        styleNames.forEach(name => {
          styles = {
            ...styles,
            ...applyFontStyle(name),
          };
        });
      }
      return styles;
    }, {});
  }
  return fontStyle;
}

// this key is being used for storing the old
// editorState of the active component for undo/redo purposes
let oldActiveEditorState = null;

class CustomRevvEditor extends Component {
  compositeDecorator = getCompositeDecorator(
    this.props.updateFormRowDataField,
    this.props.updateDynamicFieldsInDoc,
    this.props.updateDynamicFieldsInMasterDoc,
    this.props.updateEditorStateInSectionForClauseOptions,
    this.props.executeCommand,
    readDataFromLocalStorage("revvsalesUser"),
    this.props.section ? this.props.section.id : null,
  );

  state = {
    localEditorState: null,
    editorComponentId: null,
    showTableAPIHeaderEditPopup: false,
  };

  // "revvThrottle" : this method must be called only once,
  // otherwise it will create the multiple execution contexts of "revvThrottle" and
  // revvThrottle will not work as expected
  _revvThrottle = throttle(
    editorState => {
      this._saveEditor(editorState);
    },
    2000,
    { leading: false, trailing: true },
  );

  // to delay execution of api calls when user is rapidly pressing enter key
  // on windows user can press and keep enter key
  _revvDebounce = debounce(
    editorState => {
      this._saveEditor(editorState, true);
    },
    500,
    { trailing: true },
  );

  componentDidMount() {
    // updating the local editor state
    this._initLocalEditorState();

    if (!this.props.readOnly) {
      if (!this.props.isComponentActive) {
        if (this.editEditor) {
          this.editEditor.blur();
        }
      }
      document.addEventListener("copy", this._cutAndCopyEditorContent, false);
      document.addEventListener("cut", this._cutAndCopyEditorContent, false);
    }
  }

  componentDidUpdate() {
    if (
      this.props.isComponentActive &&
      this.props.preventEditorFocusOnDrag &&
      (this.props.isHoveredComponentActive ||
        !this.props.isDocCustomTextEditor ||
        this.props.isTextAreaClOptEditor)
    ) {
      let allowMultipleFocus = false;
      if (
        !this.props.preventClickedComponentIdUpdate &&
        this.props.tableSelectionType &&
        (this.props.tableSelectionType.type === "COLUMN" ||
          this.props.tableSelectionType.type === "ROW")
      ) {
        allowMultipleFocus = true;
      }
      if (allowMultipleFocus) {
        this._focus();
      } else {
        // const editorState = this._getEditorState();
        // const hasFocus = editorState
        //   ? editorState.getSelection().getHasFocus()
        //   : false;
        // if (!hasFocus) {
        //   this._focus();
        // }
        this._focus();
      }
    }

    // Need to lock the editor's content editable behaviour in case of readOnly mode
    if (
      this.editEditor &&
      this.editEditor.editor &&
      this.editEditor.editor.editor
    ) {
      const element = this.editEditor.editor.editor;
      const firstChildEle = element.firstChild;
      if (
        firstChildEle &&
        firstChildEle.childNodes &&
        firstChildEle.childNodes.length > 0
      ) {
        firstChildEle.childNodes.forEach(ele => {
          ele.contentEditable = !this.props.readOnly;
        });
      }
    }

    // Below code are being used for updating the local editor state from outside
    if (this.props.forceUpdateEditorConfigs) {
      let deciceEditorId;
      if (this.props.componentMeta) {
        deciceEditorId = this.props.componentMeta.componentId;
        if (this.props.componentMeta.componentType === "Table") {
          deciceEditorId = this.props.componentMeta.tableCellId;
        }
      }
      if (this.props.forceUpdateEditorConfigs.componentId === deciceEditorId) {
        if (this.props.section.clauseOptions) {
          if (this.props.showForm || this.props.mode === "TEMPLATE") {
            this.setState({
              localEditorState:
                this.props.forceUpdateEditorConfigs.editorState ||
                this.props.section.editorState,
            });
          }
        } else {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({
            localEditorState: this.props.forceUpdateEditorConfigs.editorState,
          });
        }
        this.props.forceUpdateLocalEditorState(null);
      }
    }
    this._initActiveEditor(
      this.props.forceUpdateEditorConfigs &&
        this.props.forceUpdateEditorConfigs.componentId ===
          this.state.editorComponentId
        ? this.props.forceUpdateEditorConfigs.editorState
        : null,
    );
  }

  componentWillUnmount() {
    if (!this.props.readOnly) {
      document.removeEventListener(
        "copy",
        this._cutAndCopyEditorContent,
        false,
      );
      document.removeEventListener("cut", this._cutAndCopyEditorContent, false);
      if (oldActiveEditorState) {
        oldActiveEditorState = null;
      }
    }
  }

  _generateEditorState = () => {
    let editorState = EditorState.createEmpty(this.compositeDecorator);
    // Clause options text editor case
    if (
      this.props.section &&
      this.props.section.type === "HTML" &&
      this.props.section.clauseOptions &&
      this.props.section.editorState
    ) {
      editorState = this.props.section.editorState;
    }

    // Normal text/table(clause options as well) editor case
    else {
      const contentState = getContentStateFromRawSectionData(
        this.props.section,
        this.props.mode,
        this.props.componentMeta,
        this.props.documentNotes || [],
        this.props.formFieldList || [],
        this.props.isSharedDocument,
      );
      if (contentState && Object.keys(contentState).length !== 0) {
        editorState = EditorState.createWithContent(
          contentState,
          this.compositeDecorator,
        );
      }
    }
    return editorState;
  };

  _initActiveEditor = editorState => {
    if (
      this.props.isSeparateToolbar &&
      !this.props.readOnly &&
      (this.props.isDocCustomTextEditor ||
        this.props.isCustomTableTextEditor) &&
      this.props.isComponentActive &&
      !this.props.activeEditorState
    ) {
      let initEditor = false;
      if (this.props.activeEditorState) {
        const decideActiveCMPId =
          this.props.activeComponentInfo.tableCellId ||
          this.props.activeComponentInfo.componentId;
        const decideCMPId =
          this.props.componentMeta.tableCellId ||
          this.props.componentMeta.componentId;
        if (decideCMPId !== decideActiveCMPId) {
          initEditor = true;
        }
      } else {
        initEditor = true;
      }
      if (initEditor) {
        this.props.initActiveEditor(
          EditorState.moveFocusToEnd(
            editorState || this.state.localEditorState,
          ),
          this.props.section.type === "Table"
            ? this.props.tableSection
            : this.props.section,
          this.props.componentMeta,
        );
      }
    }
  };

  _initLocalEditorState = () => {
    const editorState = this._generateEditorState();
    if (this.props.isComponentActive && this.props.isSeparateToolbar) {
      this._updateOldActiveEditorState(editorState, this.props.componentMeta);
    }
    let editorComponentId = null;
    if (this.props.componentMeta) {
      editorComponentId = this.props.componentMeta.componentId;
      if (this.props.componentMeta.componentType === "Table") {
        editorComponentId = this.props.componentMeta.tableCellId;
      }
    }
    this.setState({
      localEditorState: editorState,
      editorComponentId,
    });
  };

  _getOldActiveEditorState = componentMeta => {
    if (oldActiveEditorState && componentMeta) {
      const { componentType, componentId } = componentMeta;
      let activeSectionId = componentId;
      if (componentType === "Table") {
        activeSectionId = componentMeta.tableCellId;
      }
      if (oldActiveEditorState.activeSectionId === activeSectionId) {
        return oldActiveEditorState[activeSectionId];
      }
    }
    return null;
  };

  _updateOldActiveEditorState = (editorState, componentMeta) => {
    const { componentType, componentId } = componentMeta;
    let activeSectionId = componentId;
    if (componentType === "Table") {
      activeSectionId = componentMeta.tableCellId;
    }
    const tempEditorConfig = { activeSectionId };
    tempEditorConfig[activeSectionId] = editorState;
    oldActiveEditorState = { ...tempEditorConfig };
  };

  _cutAndCopyEditorContent = event => {
    if (
      this.props.isComponentActive &&
      window.getSelection() &&
      window.getSelection().toString()
    ) {
      event.preventDefault();
      if (event && (event.type === "copy" || event.type === "cut")) {
        const editorState = this._getEditorState();
        const selectedContent = getSelectedContentState(editorState);
        const html = getEditorHTML(selectedContent);
        event.clipboardData.setData(
          "text/plain",
          window.getSelection().toString(),
        );
        event.clipboardData.setData("text/html", html);
      }
    }
  };

  _focus = () => {
    if (!this.props.readOnly && this.editEditor) {
      if (this.props.customColorDropdownStatus) {
        const editorState = this._getEditorState();
        if (!checkIfAnchorOrFocusPosHasEntity(editorState, "DYNAMICFIELD")) {
          this.editEditor.focus();
        }
      } else {
        this.editEditor.focus();
      }
    }
  };

  // update the editorState in case of the Internal or External Editor respectively
  // (Internal Editor means the editor which is being used in document,
  // editor's can be used in other places also ex: clase option editors)
  _updateEditorState = editorState => {
    if (this.props.isComponentActive) {
      if (this.props.isSeparateToolbar) {
        this.props.updateActiveEditorState(
          editorState,
          this.props.componentMeta,
        );
        const change = this._isEditorDataChanged(editorState);
        if (change) {
          this._saveEditorTrigger(editorState);
        }
        this.setState({ localEditorState: editorState });
      }
      // for internal editor state (ex: clause option editor)
      else {
        this.setState({ localEditorState: editorState });
      }
    }
    // for annotations in locked document
    else if (
      this.props.activeEditorState &&
      this.props.readOnly &&
      this.props.allowComments
    ) {
      this.props.updateActiveEditorState(editorState, this.props.componentMeta);
    } else {
      this.setState({ localEditorState: editorState });
    }
  };

  _updateLocalEditorState = localEditorState => {
    this.setState({ localEditorState });
  };

  _getEditorState = () => {
    let isEditableMode = false;
    if (
      this.props.isComponentActive &&
      this.props.isSeparateToolbar &&
      !this.props.section.lock
    ) {
      isEditableMode = true;
      if (
        this.props.mode === DOCUMENT_MODE.TEMPLATE &&
        this.props.section.clauseOptions
      ) {
        isEditableMode = false;
      }
    }

    if (
      this.props.activeEditorState &&
      this.props.activeComponentInfo &&
      this.props.componentMeta
    ) {
      const decideActiveCMPId =
        this.props.activeComponentInfo.tableCellId ||
        this.props.activeComponentInfo.componentId;
      const decideCMPId =
        this.props.componentMeta.tableCellId ||
        this.props.componentMeta.componentId;
      if (decideCMPId === decideActiveCMPId) {
        isEditableMode = true;
      } else {
        isEditableMode = false;
      }
    }

    if (
      this.props.section.type === "HTML" &&
      this.props.section.clauseOptions &&
      this.props.activeEditorState &&
      this.props.activeComponentInfo &&
      this.props.activeClauseOptionValueId
    ) {
      if (
        this.props.activeClauseOptionValueId === this.props.clauseOptionValueId
      ) {
        isEditableMode = true;
      } else {
        isEditableMode = false;
      }
    }

    if (this.props.section.type === "Table" && this.props.readOnly) {
      const { data: { attributes: { dataSource } } = {} } =
        this.props.tableSection;
      if (
        dataSource ||
        (this.props.activeComponentMeta &&
          !this.props.activeComponentMeta.tableCellEditMode)
      ) {
        isEditableMode = false;
      }
    }

    return isEditableMode
      ? this.props.activeEditorState
      : this.state.localEditorState;
  };

  /**
   * "_handleChange" :: this method will be called in two scenarios
   * 1- normal edit case (means for "Draft" stage document and editable template's editor case),
   * 2- in locked document case (means in locked document case, we are allowing editor to call it's handleChane method
   *                              to allow add note funtionality)
   */
  _handleChange = newEditorState => {
    let preventOnChange = false;
    if (
      !this.props.preventClickedComponentIdUpdate &&
      this.props.tableSelectionType &&
      (this.props.tableSelectionType.type === "COLUMN" ||
        this.props.tableSelectionType.type === "ROW")
    ) {
      // This "preventOnChange" flag is being used here in the following scenario,
      // - when table cell row/col is selected and re-position the row/col,
      // then no need to call the handleChange of editor, other wise editor will get
      // focus all the time and dragging will not work
      preventOnChange = true;
    }
    // for Internal Editors(ex: document editor)
    if (
      this.props.section.lock ||
      (this.props.readOnly && !this.props.allowComments) ||
      preventOnChange
    ) {
      return;
    }

    if (this.props.isSeparateToolbar) {
      // Here adding the conditions to prevent the change in case of "form fields"
      const editorState = this._getEditorState();
      const oldContentState = editorState.getCurrentContent();
      const newContentState = newEditorState.getCurrentContent();
      if (
        oldContentState !== newContentState &&
        checkSelectionHasEntity(newEditorState, "DYNAMICFIELD")
      ) {
        return;
      }

      if (
        newEditorState.getCurrentContent() !== editorState.getCurrentContent()
      ) {
        if (this.props.readOnly && this.props.allowComments) {
          return;
        }
        // Normal flow of edior onChange
        if (newEditorState && this.props.preventEditorFocusOnDrag) {
          this._updateEditorState(newEditorState);
        }
      } else {
        this._updateEditorState(newEditorState);
      }

      // To handle color picker dropdown and save cell new data
      if (!this.props.readOnly && this.props.handleColorPicker) {
        const change = this._isEditorDataChanged(newEditorState, editorState);
        if (change) {
          this.props.handleColorPicker();
        }
      }
    }
    // for Internal Editors(ex: clause option editor in template side)
    else {
      this.setState({ localEditorState: newEditorState });
    }
  };

  _handleRemoveRange = () => {
    const editorState = this._getEditorState();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const direction = selectionState.isBackward ? "backward" : "forward";
    const anchorOffset = selectionState.getAnchorOffset();
    const focusOffset = selectionState.getFocusOffset();
    if (anchorOffset === focusOffset) {
      return false;
    }
    const textWithoutRange = Modifier.removeRange(
      contentState,
      selectionState,
      direction,
    );
    this._updateEditorState(
      EditorState.push(editorState, textWithoutRange, "remove-range"),
    );
    return true;
  };

  _saveEditor = () => {
    const { section } = this.props;
    let isUnlinkedTableAPICell = false;

    if (section.type === "Table" && section.clauseOptions) {
      const {
        attributes: { dataSource, isUnlinkedTableAPI },
      } = this.props.tableSection.data;
      if (dataSource === "TableApi" && isUnlinkedTableAPI) {
        isUnlinkedTableAPICell = true;
      }
    }

    if (isUnlinkedTableAPICell) {
      this._saveUnlinkedTableAPICellData();
    } else {
      this._saveEditorForTextAndTableCell();
    }
  };

  _saveEditorForTextAndTableCell = () => {
    const { section, handleBlur, highlightedContent } = this.props;
    let change = true;

    // in a "debounce" case we get an old editor from the
    // method, so here we need the latest editor state
    const editorState = this._getEditorState();
    const editorTextValue = editorState
      .getCurrentContent()
      .getPlainText("\u0001");
    if (
      editorTextValue === "" &&
      this.props.metaData &&
      this.props.metaData.BLOCKTYPE &&
      editorState.getLastChangeType() === null
    ) {
      change = false;
    }

    const newSection = {
      ...section,
    };

    // if change is there, update diffState, current content, data and html
    if (change) {
      newSection.data = editorState.getCurrentContent();
      // get header of the current content
      const firstBlock = editorState.getCurrentContent().getFirstBlock();
      newSection.header = firstBlock.getText().trim();
      if (firstBlock.getType().startsWith("header")) {
        newSection.label = firstBlock.getText().trim();
      } else {
        newSection.label = newSection.header;
      }
    }

    // check if there is any highlight
    if (highlightedContent) {
      change = change || null;
      newSection.highlightedContent = highlightedContent;
    }
    newSection.componentMeta = this.props.componentMeta;

    if (this.props.isSeparateToolbar) {
      if (change) {
        // update call for the editable clause option editor in form doc
        if (this.props.isTextAreaClOptEditor) {
          this._updateTextAreaClOptField(editorState);
        } else {
          // normal editor case update call
          let oldSection = {
            ...section,
            componentMeta: this.props.componentMeta,
          };
          if (this.props.isSeparateToolbar) {
            const oldEditorState = this._getOldActiveEditorState(
              this.props.componentMeta,
            );
            if (oldEditorState) {
              oldSection = {
                ...oldSection,
                data: oldEditorState.getCurrentContent(),
              };
            }
          }
          if (
            newSection.type !== DOCUMENT_COMPONENT_TYPES.TABLE &&
            this.props.mode === "DOCUMENT" &&
            newSection.clauseOptions &&
            !this.props.showForm &&
            change
          ) {
            newSection.clauseOptions = null;
            this.props.updateDocumentComponentAction(newSection);
          }
          handleBlur(
            newSection,
            change,
            false,
            { ...oldSection },
            this.props.activeComponentInfo,
          );
          if (this.props.isSeparateToolbar) {
            this._updateOldActiveEditorState(
              editorState,
              this.props.componentMeta,
            );
          }
        }
      }
    } else {
      // for External Editors(ex: clause option editor in template side)
      handleBlur(newSection, change, false, { ...section });
    }
  };

  _saveUnlinkedTableAPICellData = () => {
    let change = true;
    const editorState = this._getEditorState();
    const editorTextValue = editorState
      .getCurrentContent()
      .getPlainText("\u0001");
    if (
      editorTextValue === "" &&
      this.props.metaData &&
      this.props.metaData.BLOCKTYPE &&
      editorState.getLastChangeType() === null
    ) {
      change = false;
    }
    if (change) {
      const {
        attributes: { tableAPIFieldId, isUnlinkedTableDocument },
      } = this.props.tableSection.data;
      const { tableCellId, tableRowId } = this.props.componentMeta;
      const [rowId, cellId] = tableCellId
        ? tableCellId.split("_")
        : [null, null];
      let cellMeta = null;
      if (this.props.cell && this.props.cell.attributes) {
        cellMeta = {
          bgColor: this.props.cell.attributes.bgColor,
        };
      }
      const data = {
        tableFieldId: tableAPIFieldId,
        text: editorTextValue,
        rich: convertToRaw(editorState.getCurrentContent()),
        rowId,
        cellId: isUnlinkedTableDocument && tableRowId === 0 ? rowId : cellId,
        cellMeta,
      };

      const updateTableAPICell =
        isUnlinkedTableDocument && tableRowId === 0
          ? this.props.updateTableAPIColData
          : this.props.updateTableAPICellData;
      updateTableAPICell(data)
        .then(() => {})
        .catch(error => {
          console.error(
            "CustomRevvEditor => _saveUnlinkedTableAPICellData => Error",
            error,
          );
        });
    }
  };

  _updateTextAreaClOptField = editorState => {
    const oldEditorState = this._getOldActiveEditorState(
      this.props.componentMeta,
    );
    let oldvalue = "";
    let oldEditorText = "";
    if (oldEditorState) {
      oldvalue = JSON.stringify(
        convertToRaw(oldEditorState.getCurrentContent()),
      );
      oldEditorText = oldEditorState.getCurrentContent().getPlainText("\u0001");
    }
    const value = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    const editorText = editorState.getCurrentContent().getPlainText("\u0001");
    this.props.updateTextAreaClauseOptionInDoc(
      {
        value: editorText ? value : null,
      },
      { value: oldEditorText ? oldvalue : null },
    );
    if (this.props.isSeparateToolbar) {
      this._updateOldActiveEditorState(editorState, this.props.componentMeta);
    }
  };

  _handleBlur = () => {
    /**
     * If it is a case of Separate Toolbar then, do the following actions,
     * 2. Nuke the Redux wala EditorState
     * 3. Deactivate the current clicked component in case of "Custom Text"
     */
    if (this.props.isSeparateToolbar) {
      if (this.props.preventClickedComponentIdUpdate) {
        if (
          this.props.isComponentActive ||
          (this.props.activeComponentMeta &&
            this.props.activeComponentMeta.componentType === "Table" &&
            this.props.clickedActiveComponentId ===
              this.props.activeComponentMeta.tableCellId)
        ) {
          this.setState({ localEditorState: this._getEditorState() }, () => {
            this.props.updateComponentMeta({});
            this.props.updateDocumentClickedComponentID(null, true, null);
            this.props.nukeActiveEditor(true);
            if (this.editEditor) {
              this.editEditor.blur();
            }
            this.props.autoUpdateTitle(this.props.mode, this.props.docID);
          });
        }
      }
    }
  };

  _removeEntity = (editorState, prevCharEntity) => {
    const currentContent = editorState.getCurrentContent();
    const { currentContent: newContent, selectionState } =
      removeEntityAndReplaceText(
        currentContent,
        prevCharEntity.entity.data.id,
        "",
        true,
        editorState,
        prevCharEntity,
      );
    const newEditorState = EditorState.set(editorState, {
      currentContent: newContent,
    });
    const updatedEditorSelection = EditorState.acceptSelection(
      newEditorState,
      selectionState,
    );
    return updatedEditorSelection;
  };

  _handleKeyCommand = command => {
    const { section, readOnly } = this.props;
    if (section.lock || readOnly) {
      return "handled";
    }

    const editorState = this._getEditorState();
    if (command === "handleNextLine") {
      const newEditorState = RichUtils.insertSoftNewline(editorState);
      this._updateEditorState(newEditorState);
      return "handled";
    }
    if (command === "split-block") {
      const selectionState = editorState.getSelection();
      // Check if both prev and next keys have "DYNAMICFIELD" entity
      if (checkSelectionHasEntity(editorState, "DYNAMICFIELD")) {
        return "handled";
      }
      // Check if the prev key has "DYNAMICFIELD" entity
      const currentContent = editorState.getCurrentContent();
      const anchorKey = selectionState.getAnchorKey();
      const block = currentContent.getBlockForKey(anchorKey);
      const position = selectionState.getAnchorOffset();
      const prevEntityID = block.getEntityAt(position - 1);
      if (prevEntityID) {
        const entity = currentContent.getEntity(prevEntityID);
        if (entity.type === "DYNAMICFIELD") {
          const newEditorState = RichUtils.insertSoftNewline(editorState);
          if (newEditorState && newEditorState !== editorState) {
            this._updateEditorState(newEditorState);
          }
          return "handled";
        }
      }

      const newEditorState = splitBlock(
        editorState,
        this.props.metaData,
        this.props.isTextAreaClOptEditor,
      );
      this._updateEditorState(newEditorState);
      return "handled";
    }
    if (command === "handleUndo") {
      this._handleBlur();
      return "handled";
    }
    if (command === "handleRedo") {
      this._handleBlur();
      return "handled";
    }
    if (command === "backspace") {
      const prevCharEntity = previousCharEntity(editorState);
      let newEditorState = editorState;
      if (prevCharEntity) {
        const { entity } = prevCharEntity;
        const entityType = entity.getType();

        if (entityType === "DYNAMICFIELD") {
          newEditorState = this._removeEntity(editorState, prevCharEntity);
        } else {
          const entityData = entity.getData();
          if (entityType === "NOTES" && entityData && entityData.noteId) {
            this.props.deleteNoteByNoteId(entityData.noteId);
          }
          newEditorState = clearEntityRanges(editorState, entityData.id);
        }
        this._updateEditorState(newEditorState);
        return "handled";
      }
      if (!prevCharEntity) {
        const removed = this._handleRemoveRange();
        if (!removed) {
          return "not-handled";
        }
        return "handled";
      }
      return "not-handled";
    }

    if (command === "bold") {
      const { newEditorState } = toggleBold(editorState);
      this._handleChange(newEditorState);
      return "handled";
    }

    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState !== undefined && newState !== null) {
      this._handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  _handlePastedFiles = file => {
    const {
      readOnly,
      isDocCustomTextEditor,
      isCustomTableTextEditor,
      isComponentActive,
      isSharedDocument,
      showForm,
    } = this.props;
    if (
      !readOnly &&
      (isDocCustomTextEditor || isCustomTableTextEditor) &&
      isComponentActive &&
      !isSharedDocument &&
      !showForm
    ) {
      this._handleBlur();
      const { componentMeta } = this.props;
      const { blockType } = componentMeta;

      if (
        file &&
        ["image/jpeg", "image/png", "image/jpg"].includes(file[0].type)
      ) {
        const newSection = emptyImage();
        if (blockType === "BODY") {
          this.props.addComponent(
            newSection,
            this.props.rowIndex + 1,
            0,
            true,
            this.props.rowIndex + 1,
            0,
            undefined,
            false,
          );
        } else if (
          blockType === "HEADER" ||
          blockType === "FOOTER" ||
          blockType === "COVERPAGE"
        ) {
          this.props.addComponent(newSection, this.props.rowIndex + 1, 0, true);
        }
        this.props.saveTempPastedImage(file, newSection.id);
      } else {
        Toastr.showToastr({
          type: "failure",
          message:
            "This file type is not supported. Try using .jpeg, .jpg or .png images.",
          timeout: 3000,
        });
      }
    }
  };

  _handlePastedText = (text, textHtml) => {
    const editorState = this._getEditorState();

    // Don't allow paste if the selection has "form fields"
    if (checkSelectionHasEntity(editorState, "DYNAMICFIELD")) {
      return "handled";
    }

    // if "text" and "textHtml" both are "null" or "undefined" then, stop pasting
    if (!text && !textHtml) return "handled";

    // ::::: AUTO_CORRECTION_CASE :::::
    // If there is only "text" and "textHtml" is undefiend, then it is a case of auto correction,
    // in this case, we'll have to update the text along with current inline styles
    if (text && !textHtml) {
      const selectionState = editorState.getSelection();
      const currentContent = editorState.getCurrentContent();
      const currentFocus = editorState.getSelection().getFocusKey();
      const inlineStyle = editorState.getCurrentInlineStyle(currentFocus);

      // replace entity text with actual text and apply no entity
      const newContentState = Modifier.replaceText(
        currentContent,
        selectionState,
        text,
        inlineStyle,
        null,
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "replace-text",
      );
      this._handleChange(newEditorState);
      return "handled";
    }
    const cleanText = sanitizeHTML(textHtml || text);
    const contentState = HTMLtoDraft(cleanText, DOCUMENT_MODE.PREVIEW);
    const updatedContentState =
      updateContentStateForMultipleStyles(contentState);
    // --- SINGLE_BLOCK_OF_TYPE_*_CASE ---
    // Here, if the pasted content("updatedContentState") contains only one block,
    // then, in this case "replaceWithFragment" method removes the block type and makes it "unstyled"
    // So, in this case, updating that block type for updated editor state
    const blocks = updatedContentState.getBlocksAsArray();
    let singleBlockPastedTextType = null;
    if (blocks && blocks.length === 1) {
      const block = blocks[0];
      const blockType = block.getType();
      if (
        blockType.includes("header") ||
        blockType.includes("unordered-list") ||
        blockType.includes("ordered-list")
      ) {
        singleBlockPastedTextType = blockType;
      }
    }

    const newContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      updatedContentState.blockMap,
    );

    let newEditorState = EditorState.push(
      editorState,
      newContent,
      "insert-fragment",
    );
    // --- --- SINGLE_BLOCK_OF_TYPE_*_CASE --- ---
    if (singleBlockPastedTextType) {
      const newContentForSingleBloc = Modifier.setBlockType(
        newContent,
        newEditorState.getSelection(),
        singleBlockPastedTextType,
      );
      newEditorState = EditorState.set(newEditorState, {
        currentContent: newContentForSingleBloc,
      });
    }
    this._handleChange(newEditorState);
    return "handled";
  };

  // This method is being used for initiaing the current component meta(active component data)
  _handleEditEditorClick = () => {
    if (
      !this.props.readOnly &&
      (this.props.isDocCustomTextEditor ||
        this.props.isCustomTableTextEditor) &&
      !this.props.isComponentActive
    ) {
      let initEditor = false;
      if (this.props.section.type === "HTML") {
        initEditor = true;
        this.props.updateComponentMeta({
          ...this.props.componentMeta,
        });
      }

      if (this.props.isSeparateToolbar) {
        if (this.props.section.type === "Table") {
          if (this.props.preventClickedComponentIdUpdate) {
            initEditor = true;
          } else if (this.props.clickedActiveComponentId) {
            initEditor = false;
          } else {
            initEditor = true;
          }
        }
        if (initEditor) {
          this.props.initActiveEditor(
            this.state.localEditorState,
            this.props.section.type === "Table"
              ? this.props.tableSection
              : this.props.section,
            this.props.componentMeta,
          );
          this._updateOldActiveEditorState(
            this.state.localEditorState,
            this.props.componentMeta,
          );
        }
      }
    }
  };

  // This method is being used for initiaing the current component meta(active component data)
  // for comments addition in editor
  _handleEditorOnMouseDown = e => {
    if (
      this.props.isCustomTableTextEditor &&
      this.props.toggleShiftClickUsage
    ) {
      this.props.toggleShiftClickUsage(e.shiftKey);
    }
    if (this.props.readOnly && this.props.allowComments) {
      if (
        (this.props.activeComponentInfo &&
          this.props.activeComponentInfo.componentId) !==
        this.props.componentMeta.componentId
      ) {
        let initEditor = false;
        if (this.props.section.type === "Table" && !this.props.section.lock) {
          if (this.props.readOnly) {
            const { data: { attributes: { dataSource } } = {} } =
              this.props.tableSection;
            if (dataSource) {
              initEditor = false;
            } else {
              initEditor = true;
            }
          } else {
            initEditor = true;
          }
        } else if (this.props.section.type === "HTML") {
          initEditor = true;
        }
        if (initEditor) {
          this.props.initActiveEditor(
            this.state.localEditorState,
            this.props.section.type === "Table"
              ? this.props.tableSection
              : this.props.section,
            this.props.componentMeta,
            this.props.clauseOptionValueId,
          );
        }
      }
    }
  };

  _handleEditorOnBlur = () => {
    // to update cell data in redux when using tab / shift+tab to change the cell

    const e = window.event;
    // for handling clicks and focus of formula pane
    if (
      e.relatedTarget &&
      e.relatedTarget.dataset &&
      e.relatedTarget.dataset.name === "apply-formula-button"
    ) {
      return;
    }
    // for handling on tab and shift+tab cell change and activation
    if (e.type === "keydown" && this.props.section.type === "Table") {
      const asciiVal = e.key.charCodeAt(0);
      if (e.key.length === 1 && asciiVal > 32 && asciiVal < 127) {
        return;
      }
    }
    if (!this.props.readOnly) {
      if (this.props.isSeparateToolbar) {
        if (this.props.isDocCustomTextEditor) {
          this._handleBlur();
        } else if (
          this.props.isCustomTableTextEditor &&
          this.props.isComponentActive
        ) {
          this._handleBlur();
        }
      } else {
        const newSection = {
          ...this.props.section,
          data: this.state.localEditorState.getCurrentContent(),
        };
        this.props.handleBlur(newSection);
      }
    } else if (this.props.allowComments) {
      if (this.props.section.type === "Table") {
        this.props.nukeActiveEditor();
        // !this.props.shiftClickUsed check is to prevent blur when
        // shift + click is used for selection
        if (this.props.readOnly && !this.props.shiftClickUsed) {
          // Need to put condition for table cell
          this._handleBlur();
        }
      } else this.props.nukeActiveEditor();
    }
  };

  // if decorator is missing or incorrect then apply it
  _fixEditorDecorator = editorState => {
    if (
      editorState &&
      editorState instanceof EditorState &&
      !(editorState.getDecorator() instanceof CompositeDecorator)
    ) {
      return EditorState.set(editorState, {
        decorator: this.compositeDecorator,
      });
    }

    return editorState;
  };

  /**
   * Here, getting the correct editor placeholder,
   * 1. "Start typing..." ---> if the editor has no text or empty editor's
   *                           first block type is other than "unordered-list" or "ordered-list".
   * 2. "" otherwise
   */
  _getEditorPlaceholder = editorState => {
    let placeholder = "";
    if (
      !this.props.readOnly &&
      this.props.isComponentActive &&
      !editorState.getCurrentContent().hasText()
    ) {
      const blocks = editorState.getCurrentContent().getBlocksAsArray();
      if (blocks && blocks.length === 1) {
        const block = blocks[0];
        const blockType = block.getType();
        if (
          !(
            blockType.includes("unordered-list") ||
            blockType.includes("ordered-list")
          )
        ) {
          placeholder = "Start typing...";
        }
      }
    }
    return placeholder;
  };

  _getStyling = editorState => {
    const { metaData } = this.props;
    const selection = editorState.getSelection();
    const anchorOffset = selection.getAnchorOffset();
    const focusOffset = selection.getFocusOffset();
    const editorTextValue = editorState
      .getCurrentContent()
      .getPlainText("\u0001");
    const listStylesToCheck = [
      "FONTSIZE",
      "FONTCOLOR",
      "FONTFAMILY",
      "FONTWEIGHT",
      "BOLD",
      "ITALIC",
      "UNDERLINE",
      "BLOCKTYPE",
    ];
    if (
      metaData &&
      selection.isCollapsed() &&
      anchorOffset === 0 &&
      focusOffset === 0 &&
      editorTextValue === ""
    ) {
      listStylesToCheck.forEach(style => {
        editorState = applyStyleInEditor(editorState, metaData, style);
      });
    }
    return editorState;
  };

  _isEditorDataChanged = newEditorState => {
    const oldEditorState = this._getEditorState();
    if (
      newEditorState.getCurrentContent() !== oldEditorState.getCurrentContent()
    ) {
      return true;
    }

    // when state doesn't change but styling, DF etc is changed deep check is required
    let oldCurrentContentRaw = convertToRaw(oldEditorState.getCurrentContent());
    let newCurrentContent;
    if (newEditorState) {
      newCurrentContent = convertToRaw(newEditorState.getCurrentContent());
    }

    // sorting arrays to make sure isEqual works for arrays props as well
    oldCurrentContentRaw = sortContentState(oldCurrentContentRaw);
    newCurrentContent = sortContentState(newCurrentContent);

    const change = !isEqual(oldCurrentContentRaw, newCurrentContent);
    return change;
  };

  _saveEditorTrigger(editorState, forceImmediate) {
    const immediate = shouldSaveStateImmediately(editorState);
    if (immediate || forceImmediate) {
      this._revvThrottle.cancel(); // cancel throttled calls
      this._revvDebounce(editorState);
    } else {
      this._revvThrottle(editorState); // schedule time based throttle
    }
  }

  _handleOnTab = e => {
    e.preventDefault();
    const {
      tableSection: {
        data: {
          attributes: { cellNeighbours },
        },
      },
      activeComponentMeta,
    } = this.props;
    if (e.shiftKey && e.keyCode === 9 && activeComponentMeta) {
      const { tableRowId, tableColId } = activeComponentMeta;
      const { left } = cellNeighbours[`${tableRowId}-${tableColId}`];
      this.props.nukeActiveEditor(true);
      this._selectCellOnNavigate(e, left[0], left[1]);
    }
    if (!e.shiftKey && e.key === "Tab" && activeComponentMeta) {
      const { tableRowId, tableColId } = activeComponentMeta;
      const { right } = cellNeighbours[`${tableRowId}-${tableColId}`];
      this.props.nukeActiveEditor(true);
      this._selectCellOnNavigate(e, right[0], right[1]);
    }
  };

  _handleKeyDown = e => {
    e.stopPropagation();
    if (
      this.props.activeComponentMeta &&
      this.props.activeComponentMeta.componentType === "Table"
    ) {
      const {
        tableSection: {
          data: {
            attributes: { cellNeighbours, isTableADynamicField },
          },
        },
        activeComponentMeta,
      } = this.props;
      if (e.key === "Escape") {
        // case 1: active state to selection state
        if (activeComponentMeta.tableCellEditMode) {
          this.props.updateComponentMeta({
            ...activeComponentMeta,
            tableCellEditMode: false,
          });
        }
        // case 2: selection state to inactive state
        if (!activeComponentMeta.tableCellEditMode) this._handleBlur();
      }
      const allowedNavigations = [
        "ArrowRight",
        "ArrowLeft",
        "ArrowUp",
        "ArrowDown",
      ];
      const keys = { 37: 1, 38: 1, 39: 1, 40: 1 };
      if (activeComponentMeta && !activeComponentMeta.tableCellEditMode) {
        const { tableRowId, tableColId } = activeComponentMeta;
        if (e.key === "ArrowRight") {
          if (e.shiftKey) {
            this._makeCellSelectionShftArrow(activeComponentMeta, "ArrowRight");
          } else {
            const { right } = cellNeighbours[`${tableRowId}-${tableColId}`];
            this._selectCellOnNavigate(e, right[0], right[1]);
          }
        }
        if (e.key === "ArrowLeft") {
          if (e.shiftKey) {
            this._makeCellSelectionShftArrow(activeComponentMeta, "ArrowLeft");
          } else {
            const { left } = cellNeighbours[`${tableRowId}-${tableColId}`];
            this._selectCellOnNavigate(e, left[0], left[1]);
          }
        }
        if (e.key === "ArrowUp") {
          const { top } = cellNeighbours[`${tableRowId}-${tableColId}`];
          this._selectCellOnNavigate(e, top[0], top[1]);
        }
        if (e.key === "ArrowDown") {
          const { bottom } = cellNeighbours[`${tableRowId}-${tableColId}`];
          this._selectCellOnNavigate(e, bottom[0], bottom[1]);
        }
      }
      if (
        !isTableADynamicField &&
        !allowedNavigations.includes(e.key) &&
        activeComponentMeta &&
        activeComponentMeta.tableCellEditMode === false &&
        !this.props.tableSection.data.data[activeComponentMeta.tableRowId].data[
          activeComponentMeta.tableColId
        ].attributes.appliedFormula
      ) {
        this._checkForChars(e);
      }
      if (
        keys[e.keyCode] &&
        activeComponentMeta &&
        activeComponentMeta.componentType === "Table" &&
        !activeComponentMeta.tableCellEditMode
      ) {
        e.preventDefault();
      }
    }
  };

  _makeCellSelectionShftArrow = (meta, arrow) => {
    let selection = {};
    const { tableColId, tableRowId, cellSelection } = meta;
    const maxLimit =
      this.props.tableSection.data.data[tableRowId].attributes.columnCount - 1;
    if (!cellSelection) {
      selection = {
        rows: [tableRowId],
        cols: checkColVals(
          [
            tableColId,
            arrow === "ArrowRight" ? tableColId + 1 : tableColId - 1,
          ],
          maxLimit,
        ),
      };
    } else {
      selection = {
        rows: [tableRowId],
        cols: checkColVals(
          [
            cellSelection.cols[0],
            arrow === "ArrowRight"
              ? cellSelection.cols[1] + 1
              : cellSelection.cols[1] - 1,
          ],
          maxLimit,
        ),
      };
    }
    this.props.updateComponentMeta({
      ...meta,
      cellSelection: selection.cols[1] === selection.cols[0] ? null : selection,
    });
  };

  _checkForChars = e => {
    let isTableAPIHeaderCell = false;
    const {
      activeComponentMeta: { tableRowId } = {},
      tableSection: {
        data: {
          attributes: {
            dataSource,
            isUnlinkedTableAPI,
            isUnlinkedTableDocument,
          },
        },
      },
    } = this.props;
    if (
      !isUnlinkedTableDocument &&
      tableRowId === 0 &&
      dataSource === "TableApi" &&
      isUnlinkedTableAPI
    ) {
      isTableAPIHeaderCell = true;
    }

    const asciiVal = e.key.charCodeAt(0);
    if (e.key.length === 1 && asciiVal > 32 && asciiVal < 127) {
      if (isTableAPIHeaderCell) {
        this.props.updateDocumentClickedComponentID(
          this.props.activeComponentMeta.tableCellId,
          false,
          this.props.activeComponentMeta.componentType,
        );
        this.setState({
          showTableAPIHeaderEditPopup: true,
        });
      } else {
        const { activeComponentMeta } = this.props;
        this.props.updateComponentMeta({
          ...activeComponentMeta,
          tableCellEditMode: true,
        });
        this.props.updateDocumentClickedComponentID(
          activeComponentMeta.tableCellId,
          true,
          activeComponentMeta.componentType,
        );
      }
    }
    if (e.key === "Enter" || e.key === "Backspace") {
      const { activeComponentMeta } = this.props;
      this._selectCellOnNavigate(
        e,
        activeComponentMeta.tableRowId,
        activeComponentMeta.tableColId,
      );
      if (isTableAPIHeaderCell) {
        this.props.updateDocumentClickedComponentID(
          this.props.activeComponentMeta.tableCellId,
          false,
          this.props.activeComponentMeta.componentType,
        );
        this.setState({
          showTableAPIHeaderEditPopup: true,
        });
      } else {
        this.props.updateComponentMeta({
          ...activeComponentMeta,
          tableCellEditMode: true,
        });
      }
    }
  };

  _confirmEditTableAPIHeader = activeComponentMeta => {
    this.props.updateComponentMeta({
      ...activeComponentMeta,
      tableCellEditMode: true,
    });
    this.props.updateDocumentClickedComponentID(
      activeComponentMeta.tableCellId,
      true,
      activeComponentMeta.componentType,
    );
  };

  _selectCellOnNavigate = (e, rowId, colId) => {
    if (
      this.props.tableSection.data.data[rowId].data[colId] &&
      this.props.tableSection.data.data[rowId].data[colId].attributes &&
      this.props.tableSection.data.data[rowId].data[colId].attributes.id
    ) {
      const newCellId =
        this.props.tableSection.data.data[rowId].data[colId].attributes.id;
      this.props.updateFocusID(e, rowId, colId, newCellId);
    }
  };

  _getClassName = () => {
    let className = "editor";
    if (this.props.isComponentActive) className += " active-editor";
    if (this.props.readOnly) className += " locked-editor";
    if (this.props.section.type === "Table") className += " hand-cursor";
    else className += " text-cursor";
    return className;
  };

  render() {
    let editorState = this._getEditorState();
    // Only for html conversion on Server side rendering
    if (typeof window === "undefined") {
      const ssrEditorState = this._generateEditorState();
      const className = this._getClassName();
      return (
        <div
          id={`edit_editor_id_${this.props.section.id}`}
          className={className}
          role="button"
          tabIndex="0">
          {ssrEditorState && ssrEditorState instanceof EditorState && (
            <Editor
              section={this.props.section}
              customStyleMap={FONT_STYLE_OPTIONS}
              customStyleFn={customStyleFn}
              editorState={ssrEditorState}
              placeholder={this._getEditorPlaceholder(ssrEditorState)}
              keyBindingFn={customKeyBinding}
              blockRendererFn={customBlockRenderer(
                this._getEditorState,
                this._updateEditorState,
                this.props.readOnly,
                this.props.metaData,
                this.props.isCustomTableTextEditor,
              )}
              plugins={plugins}
              ref={element => {
                this.editEditor = element;
              }}
              readOnly={this.props.allowComments ? false : this.props.readOnly}
              data-test="customRevvEditor"
              handleDrop={() => "handled"}
            />
          )}
        </div>
      );
    }

    // when editor state is updated from multiple places it becomes null in bertween renders
    if (!editorState) return null;

    editorState = this._fixEditorDecorator(editorState);
    if (
      editorState.getSelection().isCollapsed() &&
      editorState.getSelection().getAnchorOffset() === 0 &&
      editorState.getCurrentInlineStyle().size === 0 &&
      this.props.isComponentActive
    ) {
      editorState = this._getStyling(editorState);
    }
    const className = this._getClassName();
    return (
      <>
        {this.state.showTableAPIHeaderEditPopup && (
          <EditTableApiHeaderPopup
            closePopup={() => {
              this.setState({ showTableAPIHeaderEditPopup: false });
            }}
            onConfirm={this._confirmEditTableAPIHeader}
            header="Are you sure you want edit header cell?"
            subHeader={
              "Editing header cell might cause some issues in downstream actions. Incase you want to restore the data, click on the restore button in the dropdown."
            }
            checkboxLabel="Yes, I want to edit header cell"
            okBtnText="Edit header"
            component={this.props.section}
            componentMeta={{ ...this.props.activeComponentMeta }}
          />
        )}
        <div
          id={`edit_editor_id_${this.props.section.id}`}
          className={className}
          onClick={this._handleEditEditorClick}
          onMouseDown={e => this._handleEditorOnMouseDown(e)}
          onKeyDown={e => {
            if (
              this.props.activeComponentMeta &&
              this.props.activeComponentMeta.componentType === "Table"
            )
              this._handleKeyDown(e);
          }}
          onBlur={e => this._handleEditorOnBlur(e)}
          role="button"
          tabIndex="0">
          {/**
           * ::===:: CustomRevvToolbar ::===::
           * It is being used here only for the scenarios where
           * Editor and Toolbar come together(ex: add clause option's Text Editor and
           * form mode text_area Text Editor)
           */}
          {!this.props.readOnly &&
            this.props.isComponentActive &&
            !this.props.isSeparateToolbar && (
              <CustomRevvToolbar
                mode={this.props.mode}
                section={this.props.section}
                updateLocalEditorState={this._updateLocalEditorState}
                customToolbarRef={this.props.customToolbarRef}
                isSeparateToolbar={false}
                localEditorState={editorState}
              />
            )}
          {editorState && editorState instanceof EditorState && (
            <Editor
              section={this.props.section}
              customStyleMap={FONT_STYLE_OPTIONS}
              customStyleFn={customStyleFn}
              editorState={editorState}
              placeholder={this._getEditorPlaceholder(editorState)}
              onChange={this._handleChange}
              handleKeyCommand={this._handleKeyCommand}
              handlePastedText={this._handlePastedText}
              handlePastedFiles={this._handlePastedFiles}
              keyBindingFn={customKeyBinding}
              blockRendererFn={customBlockRenderer(
                this._getEditorState,
                this._updateEditorState,
                this.props.readOnly,
                this.props.metaData,
                this.props.isCustomTableTextEditor,
              )}
              onTab={e => {
                if (
                  this.props.activeComponentMeta &&
                  this.props.activeComponentMeta.componentType === "Table"
                )
                  this._handleOnTab(e);
              }}
              plugins={plugins}
              ref={element => {
                this.editEditor = element;
              }}
              readOnly={this.props.allowComments ? false : this.props.readOnly}
              data-test="customRevvEditor"
              handleDrop={() => "handled"}
            />
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    highlightedContent,
    activeEditorState,
    activeComponentInfo,
    forceUpdateEditorConfigs,
    activeClauseOptionValueId,
  } = state.EditorStore;
  const {
    document: { showForm, id: docID, meta_data: docMetaData } = {},
    preventClickedComponentIdUpdate,
    customColorDropdownStatus,
    preventEditorFocusOnDrag,
    clickedActiveComponentId,
    tableSelectionType,
  } = state.DocumentStore;
  const {
    template: { meta_data: tempMetaData },
  } = state.TemplateStore;

  const { documentNotes } = state.NotesStore;

  let metaData;

  if (ownProps.mode === "DOCUMENT") {
    metaData = docMetaData;
  }

  if (ownProps.mode === "TEMPLATE") {
    metaData = tempMetaData;
  }

  const currentSectionEditorIdType =
    ownProps.section.type === "Table" ? "tableCellId" : "componentId";

  if (
    activeEditorState &&
    activeComponentInfo &&
    ownProps.componentMeta &&
    (ownProps.isComponentActive ||
      activeComponentInfo[currentSectionEditorIdType] ===
        ownProps.componentMeta[currentSectionEditorIdType])
  ) {
    return {
      activeEditorState,
      activeComponentInfo,
      highlightedContent,
      showForm,
      docID,
      customColorDropdownStatus,
      preventClickedComponentIdUpdate,
      preventEditorFocusOnDrag,
      metaData,
      clickedActiveComponentId,
      forceUpdateEditorConfigs,
      documentNotes,
      tableSelectionType,
      activeClauseOptionValueId,
    };
  }

  if (
    forceUpdateEditorConfigs &&
    ownProps.componentMeta &&
    forceUpdateEditorConfigs.componentId ===
      ownProps.componentMeta[currentSectionEditorIdType]
  ) {
    return {
      highlightedContent,
      showForm,
      docID,
      preventEditorFocusOnDrag,
      documentNotes,
      forceUpdateEditorConfigs,
    };
  }

  return {
    highlightedContent,
    showForm,
    docID,
    preventEditorFocusOnDrag,
    documentNotes,
  };
};

const mapDispatchToProps = {
  deleteNoteByNoteId,
  // ------ Active editor related actions methods
  initActiveEditor,
  updateActiveEditorState,
  nukeActiveEditor,
  forceUpdateLocalEditorState,
  // ----------------------
  updateComponentMeta,
  // updateComponentMetaForNotes,
  updateComponentEditorState,
  updateDocumentComponentAction,
  // forceUpdateComponentEditorState,
  autoUpdateTitle,
  updateTableAPICellData,
  updateTableAPIColData,

  updateFormRowDataField,
  updateDynamicFieldsInDoc,
  updateDynamicFieldsInMasterDoc,
  updateEditorStateInSectionForClauseOptions,
  executeCommand,
  saveTempPastedImage,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomRevvEditor);
